import { useAccountContext } from '@/lib/accounts/context';
import { getCellByPipelineId, isRecordsLikeCell } from '@/core/cell';
import { getNodes, useExplorationDataQuery } from '@/graphql';
import { MultiselectVariableDefinition, RecordsLikeCell } from '@/explore/types';
import { filterVariablesForPipeline } from '@/explore/utils';
import { dereferencePipeline } from '@/explore/pipeline/utils';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';

import { SearchInput } from '@/components/form/search-input';
import { OptionValue } from '@/components/form/types';

import { useExplorationContext } from '../exploration-context';

interface ArrayInputProps {
  variable: MultiselectVariableDefinition;
  value: MultiselectVariableDefinition['defaultValue'];
  onChange: (value: MultiselectVariableDefinition['defaultValue']) => void;
}

export const ArrayInput = (props: ArrayInputProps) => {
  const { variable } = props;
  const { exploration } = useExplorationContext();

  const numericOptions = variable.options.every(({ value }) => typeof value === 'number');

  const handleChange = (value: (string | number)[]) => {
    props.onChange(numericOptions ? value.map(Number) : value);
  };

  // This should be props.variable.source === 'static' once we parse exploration state with zod schema.
  if (props.variable.source !== 'query') {
    return (
      <SearchInput
        options={props.variable.options.map(({ value }) => ({
          label: String(value),
          value: String(value),
        }))}
        value={props.value.map((s) => String(s))}
        onChange={handleChange}
        closeOnSelect={false}
        showShortcuts
      />
    );
  }

  const cell = getCellByPipelineId(exploration.view.cells, props.variable.queryOptions.pipelineId);

  if (cell === undefined || !isRecordsLikeCell(cell)) {
    return (
      <SearchInput
        options={props.variable.options.map(({ value }) => ({
          label: String(value),
          value: String(value),
        }))}
        value={props.value.map((s) => String(s))}
        onChange={props.onChange}
        closeOnSelect={false}
        showShortcuts
      />
    );
  }

  return <DynamicTagSelect cell={cell} {...props} />;
};

const DynamicTagSelect = (props: ArrayInputProps & { cell: RecordsLikeCell }) => {
  const { exploration, getVariables } = useExplorationContext();
  const { account, isFeatureEnabled } = useAccountContext();
  const pipeline = dereferencePipeline(props.cell.pipeline, exploration);
  const variables = filterVariablesForPipeline(pipeline, getVariables());

  const queryVariables = useMemoizeDataQueryVariables({
    accountId: account.accountId,
    baseModelId: pipeline.baseModelId,
    pipeline: [
      ...pipeline.operations,
      {
        operation: 'groupAggregate',
        parameters: {
          groups: [{ key: props.variable.queryOptions.field }],
          aggregations: [
            { type: 'count', property: { key: '_sup_variable_distinct', name: 'Count' } },
          ],
        },
      },
    ],
    variables,
    options: {
      labels: { exploration: exploration.explorationId, block: props.cell.id },
      optimize: isFeatureEnabled('optimizeQuery'),
    },
    first: 500,
  });

  const { data } = useExplorationDataQuery({
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  });

  const options = getNodes(data?.account?.query)
    .map<OptionValue | null>((item) => item[props.variable.queryOptions.field])
    .filter((item) => item !== null)
    .map((item) => ({ value: item, label: String(item ?? '') }))
    .sort((a, b) => a.label.localeCompare(String(b.label)));

  const numericOptions = options.every(({ value }) => typeof value === 'number');

  return (
    <SearchInput
      options={options}
      value={props.value}
      onChange={(value) => props.onChange(numericOptions ? value.map(Number) : value)}
      closeOnSelect={false}
      showShortcuts
    />
  );
};
